import {isString, find, trim, includes, every, some} from 'lodash';
import {
  hasValue,
  isNumeric as isNumericish,
  toNumber,
} from '@packages/systems/core/utils/JsUtils';
import * as FileUploadUtils from '@packages/utilities/file-upload';
import {splitCssValue} from '@packages/systems/core/utils/CssStringUtils';
import * as ColorUtils from '@packages/systems/core/utils/ColorUtils';
import {emptyObject} from '@packages/utilities/fp/utils';
import {isSwatchPlaceholder} from '@packages/systems/style-legacy/swatches';
import {containsCssCustomValue} from '@packages/systems/style/raw-values';
import * as StyleVariables from '@packages/systems/core/utils/StyleVariables';

import type {Unit} from './cssUnitUtilsTypes';

/**
 * Converts something like "linear-gradient(45deg, red, blue, green)" to { outside: 'linear-gradient', inside: '45deg, red, blue, green' }
 * @param  {String}   str         The original CSS text.
 * @param  {Boolean}  trimQuotes  If true, quotes will be removed from the inside value.
 * @return {Object}               The parsed parts in { outside: ..., inside: ... } format.
 */
export function unwrapValue(str: string, trimQuotes?: boolean) {
  const openParen = str.indexOf('(');
  const closeParen = str.lastIndexOf(')');

  // Require well-formed parens
  if (openParen < 0 || closeParen < 0) {
    return {outside: null, inside: str};
  }

  const outside = str.substr(0, openParen);
  let inside = str.substr(openParen + 1, closeParen - openParen - 1);

  if (trimQuotes) {
    // Trim leading/trailing quotes
    inside = inside.replace(/^(\"|\')|(\"|\')$/g, '');
  }

  return {
    outside: outside === '' ? null : outside,
    inside: inside === '' ? null : inside,
  };
}

/**
 * Extracts the key used in multi-subvalue part values (background-* and transition-*).
 * @param  {String} cssProp The CSS property, e.g. 'transition-timing-function'
 * @return {String}         The shorter key, e.g. 'timing'
 */
export function getSubvalueKey(cssProp: string) {
  return cssProp.split('-')[1];
}

/**
 * The units that we consider valid for parse. Exposed for tests only
 */
export const VALID_CSS_UNITS: ReadonlyArray<Unit> = [
  'px',
  '%',
  'em',
  'rem',
  'ch',
  'deg',
  'grad',
  'rad',
  'turn',
  'pt',
  'cm',
  'mm',
  's',
  'ms',
  'vh',
  'vw',
  'dvh',
  'dvw',
  'svh',
  'svw',
  'lvh',
  'lvw',
  'vmin',
  'vmax',
  'fr',
];

export const UNIT_FOR_UNITLESS = 'number';

export type ParsedValue = {
  raw: unknown;
  value: any;
  unit: any;
  error?: string;
};

const REGEX_BG_IMAGE_URL_REGEX = /\burl\s*\(\s*["']?([^"'\r\n,]+)["']?\s*\)/gi;

/**
 * Converts a string value like "100px" to a separated obj literal { v: 100, u: 'px' }
 * @param  {String} value   Some value that could potentially be a value+unit combo.
 * @return {Object}         If it's a valid unit value, the parsed value. Otherwise the passed in value.
 */
export function parseIfUnit<Value>(value: Value): Value | ParsedValue {
  if (isUnitValue(value)) {
    return parseValue(value);
  }

  return value;
}

/**
 * Determines if a CSS value contains a number and unit.
 * @param  {String}  cssValue The CSS style value (e.g. '20px' or 'auto')
 * @return {Boolean}          True if this is a CSS unit value.
 */
export function isUnitValue(cssValue: unknown): boolean {
  if (!cssValue || cssValue === '' || typeof cssValue !== 'string') {
    return false;
  }

  return /^(\s*\-?((\d+)|(\d+\.\d+)|(\.\d+))(px|%|em|rem|ch|deg|rad|turn|grad|pt|cm|mm|s|ms|vh|vw|dvh|dvw|lvh|lvw|svh|svw|vmin|vmax|fr)?\s*){1}$/i.test(
    cssValue.trim()
  );
}

/**
 * Determins if a CSS value looks like an angle.
 * @param  {String}  cssValue The CSS style value (e.g. '250deg')
 * @return {Boolean}          True if it looks like a degree angle.
 */
export function isAngle(cssValue: string) {
  return /^-?\d+(\.\d+)?(deg|rad|grad|turn)/i.test(cssValue);
}

/**
 * Determins if a CSS value looks like a direction.
 * @param  {String}  cssValue The CSS style value (e.g. 'to right')
 * @return {Boolean}          True if it looks like a direction value.
 */
export function isDirection(cssValue: string) {
  return /^to( (right|left|top|bottom)){1,2}/i.test(cssValue);
}

type ValueToken = ParsedValue & {
  type: 'value';
};

type OperatorToken = {
  type: 'operator';
  operator: string;
  error?: string;
};

type UnaryOperatorToken = {
  type: 'unaryOperator';
  operator: string;
  sub: Token;
  error?: string;
};

type BinaryOperatorToken = {
  type: 'binaryOperator';
  operator: string;
  left: Token;
  right: Token;
  error?: string;
};

type Token =
  | ValueToken
  | OperatorToken
  | UnaryOperatorToken
  | BinaryOperatorToken;

/**
 * Parses a CSS expression like '11px + -1px' into a Webflow CSS value like { v: 10, u: 'px' }
 * Works a lot like less expressions in how it keeps units.
 * @param  {String} str   The CSS expression string.
 * @return {Object}       The parsed value details.
 */
export function parseMultiValue(str: string): any {
  function _tokenize(_str: string) {
    const tokens: Array<
      | Token
      | any
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    > = [];
    let acc = '';
    for (let i = 0; i < _str.length; i++) {
      const c = _str.charAt(i);
      acc = _handleTokenCharacter(tokens, acc, c);
    }
    _handleTokenCharacter(tokens, acc, ''); // flush the accumulator

    const unarizedTokens = _unarizeTokens(tokens);
    return unarizedTokens;
  }

  function _commitToken(
    tokens: Array<
      | Token
      | any
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >,
    s: string
  ) {
    const token = (function () {
      if (/^(?:^|\*|\/|\+|\-)$/.test(s)) {
        return {
          type: 'operator',
          operator: s,
        };
      } else {
        const value = parseValue(s);
        const v = value.value;
        const u = value.unit;
        const error = value.error;

        const result: Token = {
          type: 'value',
          value: v,
          unit: u,
          raw: value.raw,
        };
        if (error) {
          result.error = error;
        }
        return result;
      }
    })();
    tokens.push(token);
  }

  function _handleTokenCharacter(
    tokens: Array<
      | Token
      | any
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >,
    acc: string,
    c: string
  ) {
    if (!c || /\s/.test(c)) {
      if (acc) {
        _commitToken(tokens, acc);
      }
      return '';
    } else {
      if (
        /^(?:\*|\/|\+|\-)$/.test(acc) ||
        (acc && /^(?:\*|\/|\+|\-)$/.test(c))
      ) {
        _commitToken(tokens, acc);
        return c;
      } else {
        return acc + c;
      }
    }
  }

  function _unarizeTokens(
    tokens: Array<
      | Token
      | any
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >
  ) {
    return tokens.map(function (t, i, a) {
      if (t.type === 'operator' && /^(?:\+|\-)$/.test(t.operator)) {
        const lastToken = a[i - 1] || null;
        if (!lastToken || lastToken.type === 'operator') {
          return {
            type: 'operator',
            operator: t.operator === '+' ? 'p' : 'n',
          };
        } else {
          return t;
        }
      } else {
        return t;
      }
    });
  }

  function _getTokensSignature(
    tokens: Array<
      | any
      | Token
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >
  ) {
    let result = '';

    tokens.forEach(function (token) {
      switch (token.type) {
        case 'operator':
          result += token.operator;
          break;
        case 'value':
          result += 'v';
          break;
        default:
          result += '?';
          break;
      }
    });

    for (;;) {
      const matched = /(?:^|\*|\/|\+|\-|p|n)(\+|\-)/.test(result);
      if (matched) {
        result = result.replace(
          /(^|\*|\/|\+|\-|p|n)(\+|\-)/,
          function (all, prefix, op) {
            return prefix + (op === '+' ? 'p' : 'n');
          }
        );
      } else {
        break;
      }
    }

    return result;
  }

  function _nextOperatorPivotIndex(
    tokens: Array<
      | any
      | Token
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >
  ) {
    let result = -1;

    // recurse bottom-up, pivoting right-to-left with loosest -> tightest operator

    if (result === -1) {
      [
        ['+', '-'],
        ['*', '/'],
      ].some(function (operators) {
        operators.some(function (operator) {
          for (let i = tokens.length - 1; i >= 0; i--) {
            const token = tokens[i];
            if (token.type === 'operator' && token.operator === operator) {
              result = i;
              break;
            }
          }

          return result !== -1;
        });

        return result !== -1;
      });
    }

    if (result === -1) {
      ['p', 'n'].some(function (operator) {
        const token = tokens[0];
        if (token.type === 'operator' && token.operator === operator) {
          result = 0;
        }

        return result !== -1;
      });
    }

    return result;
  }

  function _buildExprTree(
    tokens: Array<
      | any
      | Token
      | {
          error?: string;
          operator: string;
          type: 'operator';
        }
    >
  ) {
    // @ts-expect-error - TS7023 - 'recurse' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
    function recurse(
      _tokens: Array<
        | any
        | Token
        | {
            error?: string;
            operator: string;
            type: 'operator';
          }
      >
    ) {
      if (_tokens.length === 1) {
        return _tokens[0];
      } else {
        const pivotIndex = _nextOperatorPivotIndex(_tokens);
        const operator = _tokens[pivotIndex];

        if (_isUnaryOp(operator)) {
          // @ts-expect-error - TS7022 - 'sub' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
          const sub = recurse(_tokens.slice(pivotIndex + 1));
          const unaryOperatorToken: UnaryOperatorToken = operator as any;
          return {
            type: 'unaryOperator',
            operator: unaryOperatorToken.operator,
            sub,
          };
        } else {
          // @ts-expect-error - TS7022 - 'left' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
          const left = recurse(_tokens.slice(0, pivotIndex));
          // @ts-expect-error - TS7022 - 'right' implicitly has type 'any' because it does not have a type annotation and is referenced directly or indirectly in its own initializer.
          const right = recurse(_tokens.slice(pivotIndex + 1));
          const binaryOperatorToken: BinaryOperatorToken = operator as any;

          return {
            type: 'binaryOperator',
            operator: binaryOperatorToken.operator,
            left,
            right,
          };
        }
      }
    }

    return recurse(tokens);
  }

  // @ts-expect-error - TS7006 - Parameter 'token' implicitly has an 'any' type.
  function _isUnaryOp(token) {
    return token.operator === 'p' || token.operator === 'n';
  }

  function _evalExprTree(exprTreeNode: Token): ParsedValue | ValueToken {
    if (exprTreeNode.type === 'value') {
      return exprTreeNode;
    } else if (exprTreeNode.type === 'unaryOperator') {
      const operator: UnaryOperatorToken = exprTreeNode.operator as any;
      const subValue = _evalExprTree(exprTreeNode.sub);
      const result = (function () {
        if (subValue.error) {
          return subValue;
        } else {
          const v = _unaryResult(operator, subValue.value);
          const u = _isNumeric(v) ? subValue.unit : null;
          const error = _isNumeric(v)
            ? null
            : 'math error in unary ' + JSON.stringify(operator) + ' operator';

          const _result: ParsedValue = {value: v, unit: u, raw: subValue.raw};
          if (error) {
            _result.error = error;
          }
          return _result;
        }
      })();
      return result;
    } else if (exprTreeNode.type === 'binaryOperator') {
      const operator: BinaryOperatorToken = exprTreeNode.operator as any;
      const leftValue = _evalExprTree(exprTreeNode.left);
      const rightValue = _evalExprTree(exprTreeNode.right);
      const result = (function () {
        if (leftValue.error) {
          return leftValue;
        } else if (rightValue.error) {
          return rightValue;
        } else {
          const v = _binaryResult(leftValue.value, operator, rightValue.value);
          const u = _isNumeric(v)
            ? _deriveBinaryUnit(leftValue.unit, rightValue.unit)
            : null;
          const error = _isNumeric(v)
            ? null
            : 'math error in binary ' + JSON.stringify(operator) + ' operator';

          const _result: ParsedValue = {
            value: v,
            unit: u,
            raw: _deriveBinaryUnit(leftValue.raw, rightValue.raw),
          };
          if (error) {
            _result.error = error;
          }
          return _result;
        }
      })();
      return result;
    } else {
      return {
        value: null,
        unit: null,
        raw: null,
        error:
          'unknown expr tree node type ' + JSON.stringify(exprTreeNode.type),
      };
    }
  }

  function _isNumeric(n: number) {
    return !isNaN(n) && isFinite(n);
  }

  function _positiveOp(s: number) {
    return +s; // eslint-disable-line no-implicit-coercion
  }
  function _negativeOp(s: number) {
    return -s;
  }
  // @ts-expect-error - TS7006 - Parameter 'op' implicitly has an 'any' type. | TS7006 - Parameter 's' implicitly has an 'any' type.
  function _unaryResult(op, s) {
    if (typeof s === 'number') {
      switch (op) {
        case 'p':
          return _positiveOp(s);
        case 'n':
          return _negativeOp(s);
        default:
          return NaN;
      }
    } else {
      return NaN;
    }
  }

  function _multiplyOp(l: number, r: number) {
    return l * r;
  }
  function _divideOp(l: number, r: number) {
    return l / r;
  }
  function _plusOp(l: number, r: number) {
    return l + r;
  }
  function _minusOp(l: number, r: number) {
    return l - r;
  }
  // @ts-expect-error - TS7006 - Parameter 'l' implicitly has an 'any' type. | TS7006 - Parameter 'op' implicitly has an 'any' type. | TS7006 - Parameter 'r' implicitly has an 'any' type.
  function _binaryResult(l, op, r) {
    if (typeof l === 'number' && typeof r === 'number') {
      switch (op) {
        case '*':
          return _multiplyOp(l, r);
        case '/':
          return _divideOp(l, r);
        case '+':
          return _plusOp(l, r);
        case '-':
          return _minusOp(l, r);
        default:
          return NaN;
      }
    } else {
      return NaN;
    }
  }

  function _deriveBinaryUnit(l: unknown, r: unknown) {
    if (l !== null) {
      return l;
    } else {
      return r;
    }
  }

  const tokens = _tokenize(str);
  const tokensSignature = _getTokensSignature(tokens);

  if (tokensSignature.length === 0) {
    return parseValue('');
  } else if (/^(?:p|n)*v(?:(?:\*|\/|\+|\-)(?:p|n)*v)*$/.test(tokensSignature)) {
    const exprTree = _buildExprTree(tokens);
    const evalResult = _evalExprTree(exprTree);
    const v = evalResult.value;
    const u = evalResult.unit;
    const error = evalResult.error;

    const result: ParsedValue = {value: v, unit: u, raw: str};
    if (error) {
      result.error = error;
    }

    return result;
  } else {
    return {
      error: 'failed to parse multi value',
    };
  }
}

/**
 * Parses a CSS string like '10px' into a Webflow CSS value like { v: 10, u: 'px' }
 * @param  {String} str   The string CSS value.
 * @return {Object}       The parsed value details.
 */
export function parseValue(
  str: unknown,
  defaultValue?: number,
  defaultUnits?: string
): ParsedValue {
  if (typeof str !== 'string') {
    const empty: ParsedValue = {
      raw: hasValue(defaultValue)
        ? String(defaultValue) +
          (hasValue(defaultUnits) ? String(defaultUnits) : '')
        : str,
      value: hasValue(defaultValue) ? defaultValue : null,
      unit: null,
    };

    if (hasValue(defaultUnits)) {
      empty.unit = defaultUnits;
    }

    if (empty.value === null) {
      empty.error = 'Invalid CSS value: ' + String(str);
    }

    return empty;
  }

  if (!str) {
    return {
      raw: String(str),
      value: '',
      unit: null,
    };
  }

  // Clean up the input
  str = str.trim().toLowerCase();

  const result: ParsedValue = {raw: str, value: null, unit: null};
  let error = false;
  let unitsIndex;

  // Find the first non-numeric char
  // @ts-expect-error - TS18046 - 'str' is of type 'unknown'.
  for (let i = 0, j = str.length; i < j; i++) {
    // @ts-expect-error - TS18046 - 'str' is of type 'unknown'.
    const ch = str.charCodeAt(i);
    // Consider 0-9 and . and - as numeric
    if (ch !== 45 && ch !== 46 && !(ch >= 48 && ch <= 57)) {
      unitsIndex = i;
      break;
    }
  }

  // @ts-expect-error - TS18046 - 'str' is of type 'unknown'.
  const strValue = unitsIndex ? str.substr(0, unitsIndex) : str;
  // @ts-expect-error - TS18046 - 'str' is of type 'unknown'.
  const strUnits = unitsIndex ? str.substr(unitsIndex) : null;
  const isNumeric = isNumericish(strValue);
  let numValue = null;

  if (strUnits && !isNumeric) {
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
    error = 'Value is not numeric: ' + strValue;
  }

  if (isNumeric) {
    numValue =
      strValue.indexOf('.') >= 0
        ? parseFloat(strValue)
        : parseInt(strValue, 10);

    if (numValue === -0) {
      numValue = 0;
    }
  } else {
    if (['auto', 'none', 'normal'].indexOf(strValue) === -1) {
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
      error = 'Unrecognized string value: ' + strValue;
    }
  }

  if (
    strUnits &&
    strUnits !== '' &&
    ![...VALID_CSS_UNITS, UNIT_FOR_UNITLESS].includes(strUnits)
  ) {
    // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'boolean'.
    error = 'Unrecognized units: ' + strUnits;
  }

  result.value = numValue === null ? strValue : numValue;

  if (strUnits || defaultUnits) {
    result.unit = strUnits || defaultUnits;
  }

  if (error) {
    // @ts-expect-error - TS2322 - Type 'boolean' is not assignable to type 'string'.
    result.error = error;
  }

  return result;
}

/**
 * Converts a CSS value to a Webflow value object.
 * @param  {String}   cssValue    The original CSS value, e.g. '10px' or 'solid'
 * @param  {Boolean}  skipParse   If true, won't try to automatically parse to a unit value.
 * @param  {Boolean}  forceNumber If true, will try to convert the CSS value to a number.
 * @return {Object}               The Webflow value object, e.g. { v: 10, u: 'px' } or { v: 'solid' }
 */
export function toWebflowCssValueObject(
  cssValue: unknown,
  skipParse?: boolean,
  forceNumber?: boolean
) {
  if (!hasValue(cssValue)) {
    return emptyObject;
  }

  if (!skipParse && isUnitValue(cssValue)) {
    return parseValue(cssValue);
  }

  if (forceNumber) {
    return {value: toNumber(cssValue), raw: cssValue};
  }

  return {value: cssValue, raw: cssValue};
}

/**
 * Converts a webflow value+unit object into a string CSS value.
 * @param  {Object} value The object representing the full value.
 * @return {String}       The stringified value.
 */
export function renderValue(value: unknown | ParsedValue) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  const parsedValue: ParsedValue = value as any;
  if (hasValue(parsedValue.value)) {
    return parsedValue.value + (parsedValue.unit || '');
  }

  return null;
}

/**
 * Joins multi-value parts and comma-separates them. Takes into account hidden parts by commenting them out.
 * @param  {Object[]}   parts     Should be in the format: { hidden: true|false, css: 'some string' }
 * @param  {Object}     options   Options.
 * @return {String}               The joined CSS value.
 */
export function joinParts(
  parts: {
    css: string | null;
    hidden?: boolean;
  }[],
  // @ts-expect-error - Error uncovered by adding type coverage for emptyObject
  options: {
    emptyValue: unknown;
    separator?: string;
  } = emptyObject
): string | null {
  let value = '';
  const emptyValue = options.emptyValue || false;
  const separator = options.separator || ',';
  let allHidden = true;

  // If there are no parts, assume there is no value
  if (parts.length === 0) {
    return null;
  }

  // If every part's CSS is null, return null
  if (
    every(parts, function (part) {
      return part.css === null;
    })
  ) {
    return null;
  }

  parts.forEach(function (part, index) {
    const hidden = part.hidden;
    const enabled = !hidden;
    const rest = parts.slice(index + 1);
    const last = rest.length === 0;
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const prevEnabled = index > 0 && !parts[index - 1].hidden;
    const spaceSep = separator === ' ';
    const enabledLater = !spaceSep && some(rest, (s) => !s.hidden);
    const trailingSep =
      !spaceSep && !last && (hidden || (enabled && enabledLater));
    const leadingSep = !spaceSep && hidden && prevEnabled && !enabledLater;
    let css = part.css;

    if (emptyValue && css === null) {
      // @ts-expect-error - TS2322 - Type '{}' is not assignable to type 'string'.
      css = emptyValue;
    }

    if (enabled) {
      css += trailingSep ? separator : '';
      allHidden = false;
    } else {
      css =
        '/*' +
        (leadingSep ? separator : '') +
        ' ' +
        css +
        (trailingSep ? separator : '') +
        ' */';
    }

    value += css + ' ';
  });

  if (allHidden && emptyValue) {
    value = String(emptyValue) + ' ' + value;
  }

  return value.trim();
}

/**
 * Extracts a color value from the array. If it is found, it is removed from the array.
 * @param  {String[]}  cssParts The array of CSS parts, e.g. [ '1px', '0', '2px', '#fff' ]
 * @return {String}             The first located color in the array of CSS values, e.g. '#fff'
 */
export function extractColor(
  cssParts: string[],
  variableMap: StyleVariables.VariableMap
) {
  return _extractPart(cssParts, isColor(variableMap));
}

/**
 * Extracts an image value from the array. If it is found, it is removed from the array.
 * @param  {String[]}  cssParts The array of CSS parts.
 * @return {String}             The first located image value.
 */
export function extractImage(cssParts: string[]) {
  return _extractPart(cssParts, isImage);
}

/**
 * Extracts a discrete string value from the array, picking from a range of candidates. If it is found, it is removed from the array.
 * @param  {String[]}  cssParts     The array of CSS parts.
 * @param  {String[]}  matchArray   The array of values to match.
 * @return {String}                 The first located image value.
 */
export function extractValue(cssParts: string[], matchArray: string[]) {
  matchArray = Array.isArray(matchArray) ? matchArray : [matchArray];

  // @ts-expect-error - TS7006 - Parameter 'part' implicitly has an 'any' type.
  return _extractPart(cssParts, function (part) {
    return includes(matchArray, part);
  });
}

/**
 * Extracts a gradient angle value from the array. If it is found, it is removed from the array.
 * @param  {String[]}  cssParts The array of CSS parts.
 * @return {String}             The first located angle (e.g. '180deg', 'to bottom', 'to right top') value.
 */
export function extractAngle(cssParts: string[]) {
  return _extractPart(cssParts, isAngle) || _extractPart(cssParts, isDirection);
}

/**
 * Extracts the first value from the array. If the array is not empty, the first element is removed.
 * @param  {String[]}  cssParts     The array of CSS parts.
 * @return {String}                 The first value in the array.
 */
export function extractFirst(cssParts: string[]) {
  return _extractPart(cssParts, function () {
    return true;
  });
}

/**
 * Determines if a CSS value is a color.
 * @param  {String}  cssValue   A CSS string.
 * @return {Boolean}            True if the value looks like a color.
 */
export function isColor(variableMap: StyleVariables.VariableMap) {
  return function isColor_(cssValue: unknown) {
    if (cssValue === null || cssValue === undefined) {
      return false;
    }

    if (typeof cssValue !== 'string') {
      return false;
    }

    cssValue = cssValue.trim();
    if (cssValue === '') {
      return false;
    }

    if (cssValue === 'transparent') {
      return true;
    }

    // Treat @swatch_12345678 placeholders as valid CSS colors
    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
    if (isSwatchPlaceholder(cssValue)) {
      return true;
    }

    if (
      typeof cssValue === 'string' &&
      StyleVariables.isVariablePlaceholder(cssValue)
    ) {
      const variableId = StyleVariables.getVariableIdFromPlaceholder(cssValue);

      if (variableId) {
        const value = StyleVariables.resolvePreviewValue(
          variableMap,
          variableId
        );

        return (
          value.type === 'color' ||
          // We treat resolved raw values as valid colors so long as the
          // variable being referenced itself is a variable:
          (value.type === 'raw' &&
            variableMap.get(variableId)?.type === 'color')
        );
      }

      return false;
    }

    // @ts-expect-error - TS2345 - Argument of type 'unknown' is not assignable to parameter of type 'string'.
    return ColorUtils.isValidColor(cssValue);
  };
}

/**
 * Determines if a CSS value looks like a background-image value.
 * @param  {String}  cssValue   A CSS string.
 * @return {Boolean}            True if the value looks like an image.
 */
export function isImage(cssValue?: string | null) {
  if (!cssValue) {
    return false;
  }

  cssValue = cssValue.trim().toLowerCase();
  return cssValue.indexOf('url(') === 0 || cssValue.indexOf('-gradient(') > 0;
}

/**
 * Indicates whether the property can contain multiple parts (e.g. box-shadow, transform, effect, etc);
 * @param  {String}  propName The CSS property name.
 * @return {Boolean}          True if the property can have multiple values.
 */
export function isMultiPartProp(propName?: string | null) {
  if (propName == null) {
    return false;
  }
  return (
    propName !== 'background-color' &&
    propName !== 'background-clip' &&
    /^(transition(-\w+)+|background-\w+)$/im.test(propName)
  );
}

/**
 * Indicates whether a value looks like a background repeat.
 * @param  {String}  value The CSS repeat value.
 * @return {Boolean}       True if the property is 'repeat', 'no-repeat', 'repeat-x', or 'repeat-y'
 */
export function isRepeat(value?: string | null) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return /^(no\-)?repeat(-(x|y))?$/i.test(value.trim());
}

/**
 * Indicates whether a value looks like a background attachment.
 * @param  {String}  value The CSS attachment value.
 * @return {Boolean}       True if the property is valid.
 */
export function isAttachment(value?: string | null) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return /^(scroll|fixed|local)$/i.test(value.trim());
}

/**
 * Determines if a CSS value contains any 3D transform values.
 * @param  {String} value  The raw CSS value.
 * @return {Boolean}
 */
export function has3dTransform(value?: string | null) {
  if (!value || typeof value !== 'string') {
    return false;
  }

  return (
    value !== 'none' &&
    splitCssValue(value, ' ').some((prop) => {
      return /^(?:translateZ|translate3d|scaleZ|scale3d|rotateX|rotateY|rotate3d|matrix3d)\(/.test(
        prop
      );
    })
  );
}

/**
 * Cleans a block of CSS for export.
 * @param  {String}   css             The source CSS.
 * @param  {Function} urlGenerator    A function which, given a file, returns its relative url. e.g. '../images/cat.gif'.
 * @return {String}                   The cleaned CSS.
 */
export function cleanForExport(css: string, urlGenerator: any) {
  css = _shortenUrls(css, urlGenerator);
  return css;
}

/**
 * Restricts the degree to [0, 359].
 */
function normalizeCssDeg(cssDeg: number) {
  // Constrain to [-359...359]
  cssDeg %= 360;

  // If negative, need to wrap around once
  if (cssDeg < 0) {
    cssDeg += 360;
  }

  if (cssDeg === -0) {
    cssDeg = 0;
  }

  return cssDeg;
}

/**
 * Converts an angle and distance to X Y offsets.
 */
export function angleDistToXY(angle?: number | null, distance?: number | null) {
  angle = angle == null ? 0 : angle;
  distance = distance == null ? 0 : distance;

  const rad = ((90 - angle) * Math.PI) / 180;
  let x = Math.round(Math.cos(rad) * distance);
  let y = -Math.round(Math.sin(rad) * distance);

  // Make sure we don't have negative 0 values (these mess up Jasmine)
  x = x === -0 ? 0 : x;
  y = y === -0 ? 0 : y;

  return {
    x,
    y,
  };
}

/**
 * Converts an X Y offset to an angle/distance value.
 */
export function xyToAngleDist(x: number, y: number) {
  const rad = Math.atan2(-y, x);
  const dist = Math.round(Math.sqrt(x * x + y * y));
  let angle = normalizeCssDeg(90 - Math.round(rad * (180 / Math.PI)));

  // Make sure we don't have negative 0 values
  angle = angle === -0 ? 0 : angle;

  return {
    angle,
    dist,
  };
}

/**
 * Extracts the main font family from a font-family declaration.
 * @param  {String} fontCss The full font-family CSS, e.g. "'Open Sans', Arial, sans-serif"
 * @return {String}         The main font family, e.g. "Open Sans"
 */
export function getFontFamily(fontCss: string) {
  if (!fontCss || typeof fontCss !== 'string') {
    return fontCss;
  }

  // Custom raw values are an explicit opt-out of font-family normalization —
  // splitting on the comma would chop the `@raw<|...|>` wrapper mid-content
  // and corrupt the stored value (STYL-2402).
  if (containsCssCustomValue(fontCss)) {
    return fontCss;
  }

  const parts = fontCss.split(',');
  const firstFont = trim(parts[0], ' \'"');

  return firstFont;
}

/**
 * Converts long CDN background-image links to relative links. Used for export.
 * @param  {String}   cssText         The block of CSS that can contain background-image: url(...) values.
 * @param  {Function} urlGenerator    A function which, given a file, returns its relative url. e.g. '../images/cat.gif'.
 * @return {String}                   The same CSS with absolute CDN links converted to localized ones.
 */
function _shortenUrls(cssText: string, urlGenerator: any) {
  const shortened = cssText.replace(
    REGEX_BG_IMAGE_URL_REGEX,
    function (fullMatch, fullUrl) {
      const fileData = FileUploadUtils.getUploadData(fullUrl);
      const shortUrl = urlGenerator(fileData);

      // Must wrap in quotes because the file name may have parens in it
      return "url('" + shortUrl + "')";
    }
  );

  return shortened;
}

/**
 * Returns the first font-family family in a string of CSS.
 * @param  {String}   cssText   The block of CSS.
 * @return {String}             A matched font family, or null if none are found.
 */
export function findFontFamily(cssText: string) {
  if (!cssText || !isString(cssText)) {
    return null;
  }

  const matches = /\bfont-family:\s*['|"]?(\b[\w|\s]+\b)['|"]?[,|;]/i.exec(
    cssText
  );
  if (matches && matches.length > 1) {
    return matches[1];
  }

  return null;
}

function _extractPart(cssParts: string[], comparer: any) {
  const matchedValue = find(cssParts, function (part) {
    return comparer(part);
  });

  if (matchedValue) {
    // Remove the value from the array
    // @ts-expect-error - TS2345 - Argument of type 'string | number | { (callbackfn: (previousValue: string, currentValue: string, currentIndex: number, array: string[]) => string): string; (callbackfn: (previousValue: string, currentValue: string, currentIndex: number, array: string[]) => string, initialValue: string): string; <U>(callbackfn: (previousValue: U, curr...' is not assignable to parameter of type 'string'.
    cssParts.splice(cssParts.indexOf(matchedValue), 1);

    // Return the matched value
    return matchedValue;
  }

  // Value not found
  return null;
}
